import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { debounceTime, Subject, switchMap, from, catchError, EMPTY, groupBy, mergeMap, takeUntil } from 'rxjs';

@GridModalComponent('charge')
@Component({
    selector: 'tt-charge',
    templateUrl: './charge.component.html',
    styleUrls: ['./charge.component.css'],
})
export class ChargeComponent implements OnChanges, OnDestroy {
    @Input()
    public params?: ChargeParams;

    public get?: ChargeGet;

    public grid?: GridOptions;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public saving = false;

    private change$ = new Subject<UpdateChargeParams>();

    private destroy$ = new Subject<void>();

    public documentsGridTabInitiated = false;

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
    ) {
        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.updateCharge(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateChargeResponse });
        this.initFromDialog(stateDialog);
    }

    public onUpdateChargeResponse = async (response: Partial<ChargeGet>) => {
        for (let k in response) {
            let key = k as keyof ChargeGet;

            if (!!response[key] && !!this.get) this.get[key] = response[key] as any;
        }
    };

    public onChange(event: keyof ChargeGet) {
        if (!!this.get) this.change$.next({ ...this.get, colname_changed: event });
    }

    public onTabChanged() {
        if (this.get?.gb_charge_main === 'DOCUMENTS') {
            this.documentsGridTabInitiated = true;
        }
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const { errorcode, errormessage } = await this.saveCharge(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private readGrid() {
        if (!this.get || this.get.charge_keyno === '0') return;

        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('chargetest_grid')
                .setLoadDataFunction({ method: 3918, parameters: () => this.get })
                .setCustomToolbarButtons([
                    {
                        name: 'new_chargetest',
                        text: 'new_chargetest',
                        translate: true,
                        icon: 'far fa-plus',
                        cssClass: 'im-grid-btn-xs-l tt-button tt-button--primary',
                        func: async () => {
                            const response = await this.modal.openStateDialog({ state: 'chargetest', params: this.get!.charge_keyno + '/0' });

                            if (!!response) {
                                this.grid?.gridfunc?.read();
                            }
                        },
                    },
                ])
                .setSpecialFunc({
                    buttons: [
                        {
                            name: 'open_chargetest',
                            icon: 'fas fa-pen',
                            btn_type: 'primary',
                            tooltip: 'open_chargetest',
                            translateTooltip: true,
                            func: async (row: GridRow) => {
                                const response = await this.modal.openStateDialog({ state: 'chargetest', params: this.get!.charge_keyno + '/' + row['chargetest_keyno'] });

                                if (!!response) {
                                    this.grid?.gridfunc?.read();
                                }
                            },
                        },
                    ],
                });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getCharge(this.params);
        }
    }

    /**
     * Retrieves the charge with the given keyno.
     *
     * @datatask 3919
     * @datataskName sp_ws_p2_charge_get
     * @param params keyno for the charge to get.
     * @returns promise containing the charge.
     */
    private async getCharge(params: ChargeParams): Promise<ChargeGet> {
        return (await this.datatask.Post(3919, params))[0];
    }

    private async updateCharge(params: UpdateChargeParams): Promise<Partial<ChargeGet>> {
        return (await this.datatask.Post(3943, params))[0];
    }

    /**
     * Saves the given charge.
     *
     * @datatask 3920
     * @datataskName sp_ws_p2_charge_save
     * @param params the charge to save.
     * @returns promise when the save fullfils containig error message.
     */
    private async saveCharge(params: ChargeGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3920, params))[0];
    }

    private async init() {
        await this.loadGet();
        this.readGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            state.setSize({ height: '95vh', width: 'min(95vw)' });
            this.params = state.params as unknown as ChargeParams;
            await this.init();
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => state.close(value) });
            state.setHeading(this.get?.webpage_heading ?? '');
        }
    }
}

export interface ChargeParams {
    charge_keyno: string;
}

export interface ChargeGet {
    gb_charge_main: string;
    webpage_heading: string;
    charge_keyno: string;
    charge_id: string;
    charge_c: string;
    charge_si: string;
    charge_mn: string;
    charge_p: string;
    charge_s: string;
    charge_n: string;
    charge_cr: string;
    charge_ni: string;
    charge_ai: string;
    charge_cu: string;
    charge_mo: string;
    charge_nb: string;
    charge_v: string;
    charge_sn: string;
    charge_ti: string;
    charge_h2: string;
    charge_ce: string;
    reg_by_portal_user_name: string;
    reg_datetime: string;
    mod_by_portal_user_name: string;
    mod_datetime: string;
}

export interface UpdateChargeParams extends ChargeGet {
    colname_changed: keyof ChargeGet;
}
